import {inject, Injectable} from "@angular/core"
import type {CmLogger} from "@cm/log"
import {CM_LOGGER_AUTH} from "@common/services/logging"
import {localStorageToken} from "@common/helpers/auth/token"
import {CM_COLORMASS_CLIENT} from "@common/config/client-injector"

/**
 * Simple service using local storage to obtain headers required for authentication etc.
 * Must not use GQL, as that would create a circular dependency involving Apollo
 */
@Injectable({
    providedIn: "root",
})
export class LocalStorageService {
    private readonly colormassClient = inject(CM_COLORMASS_CLIENT)
    private readonly logger: CmLogger = inject(CM_LOGGER_AUTH)

    get headersWithoutSilo(): Record<string, string> {
        const token = localStorageToken()
        return token
            ? {
                  Authorization: `Bearer ${token}`,
              }
            : {}
    }

    get developmentMode(): boolean {
        try {
            const devMode = localStorage.getItem("cm-development-mode")
            return devMode === "true"
        } catch {
            return false
        }
    }

    get headers(): Record<string, string> {
        try {
            const systemRole = localStorage.getItem("cm-auth-silo-system-role")
            const organizationId = localStorage.getItem("cm-auth-silo-organization")
            const membershipId = localStorage.getItem("cm-auth-silo-membership")
            // Staff-only self-service preview override. Honored server-side only for staff (see
            // siloForToken); for everyone else it is ignored, so sending it is safe.
            const accountType = localStorage.getItem("cm-auth-silo-account-type")
            return {
                ...this.headersWithoutSilo,
                ...(systemRole ? {"X-Colormass-Auth-Silo-System-Role": systemRole} : {}),
                // no need to send the organization - it should always match the membership
                // by not sending it, we eliminate the potential for a mismatch
                ...(organizationId && !membershipId ? {"X-Colormass-Auth-Silo-Organization": organizationId} : {}),
                ...(membershipId ? {"X-Colormass-Auth-Silo-Membership": membershipId} : {}),
                ...(accountType ? {"X-Colormass-Auth-Silo-Account-Type": accountType} : {}),
            }
        } catch (error) {
            this.logger.error("Failed to load headers from local storage", error)
            return {
                "X-Colormass-Client": this.colormassClient,
            }
        }
    }

    public loadSilo() {
        try {
            const systemRole = localStorage.getItem("cm-auth-silo-system-role")
            const organizationId = localStorage.getItem("cm-auth-silo-organization")
            const membershipId = localStorage.getItem("cm-auth-silo-membership")
            // Client-only self-service preview override (never sent as a request header, see `headers`).
            const accountType = localStorage.getItem("cm-auth-silo-account-type")
            return {systemRole, organizationId, membershipId, accountType}
        } catch (error) {
            this.logger.warn("Failed to load auth silo from local storage", error)
            return {systemRole: null, organizationId: null, membershipRole: null, membershipId: null, accountType: null}
        }
    }

    public storeSilo(values: {systemRole?: string | null; organizationId?: string | null; membershipId?: string | null; accountType?: string | null}) {
        try {
            switch (values.systemRole) {
                case null:
                    localStorage.removeItem("cm-auth-silo-system-role")
                    break
                case undefined:
                    break
                default:
                    localStorage.setItem("cm-auth-silo-system-role", values.systemRole)
            }
            switch (values.organizationId) {
                case null:
                    localStorage.removeItem("cm-auth-silo-organization")
                    break
                case undefined:
                    break
                default:
                    localStorage.setItem("cm-auth-silo-organization", values.organizationId)
            }
            switch (values.membershipId) {
                case null:
                    localStorage.removeItem("cm-auth-silo-membership")
                    break
                case undefined:
                    break
                default:
                    localStorage.setItem("cm-auth-silo-membership", values.membershipId)
            }
            switch (values.accountType) {
                case null:
                    localStorage.removeItem("cm-auth-silo-account-type")
                    break
                case undefined:
                    break
                default:
                    localStorage.setItem("cm-auth-silo-account-type", values.accountType)
            }
        } catch (error) {
            this.logger.error("Failed to save auth silo to local storage", error, values)
        }
    }

    public useStoredToken = true

    public loadToken(): string | null {
        if (!this.useStoredToken) {
            return null
        }
        try {
            return localStorage.getItem("token")
        } catch {
            // probably don't have access to localStorage, default to null
            return null
        }
    }

    public storeToken(token: string | null) {
        try {
            if (token === null) {
                localStorage.removeItem("token")
            } else {
                localStorage.setItem("token", token)
            }
        } catch (error) {
            this.logger.error("Failed to store token in localStorage", error)
        }
    }

    public loadCsrfToken(): string | null {
        try {
            return localStorage.getItem("cm-csrf-token")
        } catch {
            // probably don't have access to localStorage, default to null
            return null
        }
    }

    public storeCsrfToken(token: string | null) {
        try {
            if (token === null) {
                localStorage.removeItem("cm-csrf-token")
            } else {
                localStorage.setItem("cm-csrf-token", token)
            }
        } catch (error) {
            this.logger.error("Failed to store CSRF token in localStorage", error)
        }
    }
}
